import { BeatOnset } from "./beatOnset";
import { kickWaveAtDepth } from "./kickWave";
import { FrameFrequencyResponse, mixFrameFrequencyBands, type FrameFrequencyBands } from "./lightFrameResponse";
export type ReactingMode = 1 | 2 | 4 | 5;
export const FRAME_COUNT = 10;
export const EDGE_COUNT = FRAME_COUNT * 4;
export const FRAME_SPACING = 7.3;

export const REACTING_MODES = [
  { id: 1, name: "穿行", description: "分频亮灯 · 重拍推行 · 旋转开启时重拍加速" },
  { id: 2, name: "环绕", description: "重拍驱动顺时针追光" },
  { id: 4, name: "对拍", description: "横边与竖边交替回应" },
  { id: 5, name: "碎光", description: "瞬态点亮独立边缘" },
] as const;

/** Restore legacy stored preset IDs without changing the saved effect. */
export function normalizeReactingMode(value: number): ReactingMode {
  return value === 2 || value === 4 || value === 5 ? value : 1;
}

export interface LightFrameAudio {
  /** One independent frequency band per edge, ordered top/right/bottom/left. */
  bands: number[];
  bass: number;
  treble: number;
  frequencyBands?: FrameFrequencyBands & { rangeKey: string };
}

interface BeatPulse { age: number; strength: number; parity: number }
const unit = (n: number) => Number.isFinite(n) ? Math.max(0, Math.min(1, n)) : 0;
const ROTATION_PACE = { 1: 0.48, 2: 0.7, 4: 0.95, 5: 0.58 };

/** Stateful audio choreography; no DOM, frame-count clocks, or random flicker. */
export class LightFrameEngine {
  private edges = new Float32Array(EDGE_COUNT);
  private rotations = new Float32Array(FRAME_COUNT);
  private angularSpeeds = new Float32Array(FRAME_COUNT);
  private angularImpulses = new Float32Array(FRAME_COUNT);
  private previousBands = new Float32Array(EDGE_COUNT);
  private sparks = new Float32Array(EDGE_COUNT);
  private pulses: BeatPulse[] = [];
  // Global kick layer survives all sequence changes.
  private kickPulses: number[] = [];
  private mode: ReactingMode | null = null;
  private onset = new BeatOnset();
  private beatCount = 0;
  private expansion = 0;
  private beatEnvelope = 0;
  private travel = 0;
  private velocity = 0;
  private positions = Array.from({ length: FRAME_COUNT }, (_, index) => 2 - index * FRAME_SPACING);
  private frequencyResponse = new FrameFrequencyResponse(EDGE_COUNT);
  private frontFrame = 0;
  triggered = false;

  step(audio: LightFrameAudio, delta: number, mode: ReactingMode, threshold: number, rotation = true) {
    const dt = Number.isFinite(delta) ? Math.max(0, Math.min(0.1, delta)) : 0;
    if (dt === 0) return this.state();
    const bass = unit(audio.bass);
    const gate = unit(threshold);
    if (this.mode !== null && this.mode !== mode) {
      this.pulses = [];
      this.sparks.fill(0);
      this.velocity = 0;
    }
    this.mode = mode;
    this.pulses = this.pulses.filter((pulse) => (pulse.age += dt) < 1.4);
    this.kickPulses = this.kickPulses.map(age => age + dt).filter(age => age < 1.2);
    this.triggered = this.onset.step(bass, dt, gate);
    this.beatEnvelope *= Math.exp(-dt / 0.24);
    if (this.triggered) {
      this.beatEnvelope = 1;
      this.pulses.push({ age: 0, strength: 1, parity: this.beatCount++ % 2 });
      this.kickPulses.push(0);
    }
    this.expansion = this.beatEnvelope * 0.045;

    if (mode === 1) {
      if (this.triggered) this.velocity = 9;
      const decay = Math.exp(-dt / 0.42);
      // Integrate the decaying velocity exactly, independent of display rate.
      this.travel = (this.travel + this.velocity * 0.42 * (1 - decay)) % (FRAME_COUNT * FRAME_SPACING);
      this.velocity *= decay;
    }
    for (let index = 0; index < FRAME_COUNT; index++) {
      const z = 2 - index * FRAME_SPACING + this.travel;
      this.positions[index] = z > 9 ? z - FRAME_COUNT * FRAME_SPACING : z;
    }

    const front = this.positions.indexOf(Math.max(...this.positions));
    if (front !== this.frontFrame) {
      // Previous high-frequency momentum must not drive the newly nearest frame.
      this.angularImpulses[front] = 0;
      this.angularSpeeds[front] = 0;
      this.frontFrame = front;
    }
    const bands = audio.frequencyBands ? mixFrameFrequencyBands(audio.frequencyBands, this.positions) : audio.bands;
    const filteredFrequency = audio.frequencyBands
      ? this.frequencyResponse.step(audio.frequencyBands, audio.frequencyBands.rangeKey, dt, this.positions) : null;
    const frequencyResponse = mode === 1 ? filteredFrequency : null;
    if (filteredFrequency?.reset) {
      this.angularImpulses.fill(0);
      this.angularSpeeds.fill(0);
    }

    for (let i = 0; i < EDGE_COUNT; i++) {
      const frame = Math.floor(i / 4);
      const side = i % 4;
      const band = unit(bands[i] ?? 0);
      const rise = Math.max(0, band - this.previousBands[i]!);
      this.previousBands[i] = band;
      const transient = rise / (dt * 60);
      this.sparks[i] = Math.max(this.sparks[i]! * Math.exp(-dt / 0.12), transient > 0.012 ? unit(transient * 16) : 0);
      if (frequencyResponse) {
        this.edges[i] = frequencyResponse.edges[i]!;
        this.sparks[i] = frequencyResponse.sparks[i]!;
        continue;
      }
      let target = 0;

      switch (mode) {
        case 1:
          target = Math.pow(unit((band - 0.28) / 0.52), 2.2);
          break;
        case 2:
          for (const pulse of this.pulses) {
            const phase = (pulse.age - frame * 0.016) / 0.15;
            if (phase < -0.05 || phase > 4.2) continue;
            const distance = Math.abs(phase - side);
            target = Math.max(target, Math.exp(-distance * distance / 0.28) * pulse.strength * (0.7 + band * 0.3));
          }
          break;
        case 4:
          for (const pulse of this.pulses) {
            if (side % 2 !== pulse.parity) continue;
            const age = pulse.age - frame * 0.012;
            if (age < 0) continue;
            target = Math.max(target, pulse.strength * Math.exp(-age / 0.16) * (0.72 + band * 0.28));
          }
          break;
        case 5:
          target = this.sparks[i]!;
          break;
      }

      const previous = this.edges[i]!;
      const release = mode === 1 ? 0.22 : mode === 5 ? 0.09 : 0.065;
      const tau = target > previous ? 0.018 : release;
      this.edges[i] = unit(target + (previous - target) * Math.exp(-dt / tau));
    }

    // Each square owns its angle and velocity. Its own edges drive rotation.
    for (let frame = 0; frame < FRAME_COUNT; frame++) {
      const offset = frame * 4;
      const activity = Math.max(...this.edges.subarray(offset, offset + 4));
      const direction = frame % 2 === 0 ? 1 : -1;
      const pace = ROTATION_PACE[mode];
      const localTransient = Math.max(...this.sparks.subarray(offset, offset + 4));
      this.angularImpulses[frame] = rotation ? Math.max(this.angularImpulses[frame]! * Math.exp(-dt / 0.19), localTransient,
        this.triggered ? (mode === 1 ? 1.2 : activity * 0.75 + 0.2) : 0) : 0;
      const speed = rotation ? direction * (pace + frame * 0.035) * (this.angularImpulses[frame]! * 1.5 + activity * 0.03) : 0;
      this.angularSpeeds[frame] = rotation ? speed + (this.angularSpeeds[frame]! - speed) * Math.exp(-dt / 0.16) : 0;
      if (rotation) {
        // Keep phase continuous: tilt uses non-integer multiples of this angle.
        this.rotations[frame] = this.rotations[frame]! + this.angularSpeeds[frame]! * dt;
      } else {
        // Return along the nearest full turn, keeping rectangle aspect ratio intact.
        const angle = Math.atan2(Math.sin(this.rotations[frame]!), Math.cos(this.rotations[frame]!));
        this.rotations[frame] = angle * Math.exp(-dt / 0.22);
      }
    }
    return this.state();
  }

  private state() {
    return { edges: this.edges, rotations: this.rotations, expansion: this.expansion, travel: this.travel, positions: this.positions };
  }

  kickAtFrame(index: number) {
    const front = Math.max(...this.positions), back = Math.min(...this.positions);
    const depth = (front - this.positions[index]!) / (front - back) * (FRAME_COUNT - 1);
    return this.kickAtDepth(depth);
  }

  kickAtDepth(depth: number) {
    let light = 0;
    for (const age of this.kickPulses) light = Math.max(light, kickWaveAtDepth(age, depth, FRAME_COUNT - 1));
    return light;
  }
}
